import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface SidebarState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const SidebarContext = createContext<SidebarState | null>(null);

/**
 * Whether the narrow-screen sidebar is showing.
 *
 * Context rather than state in `AppShell`, because the button that opens it
 * lives in the topbar and the panel it opens is the sidebar — two siblings that
 * would otherwise have to be joined by prop-drilling through the frame.
 *
 * Below the prototype's 880px breakpoint the sidebar is `position: fixed` and
 * translated off-screen; `.sidebar.open` brings it back. Those rules are already
 * in the vendored stylesheet — the prototype simply never rendered the button or
 * the scrim that drive them. Nothing here is new CSS.
 */
export function SidebarProvider({ children }: { children: ReactNode }): ReactNode {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const close = useCallback(() => setIsOpen(false), []);
  const open = useCallback(() => setIsOpen(true), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  /*
   * Following a link closes it. On a phone the sidebar covers the page, so
   * leaving it open after navigation would hide the screen the user just asked
   * for.
   */
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  /*
   * Escape closes it, on the document rather than on the panel: focus may be
   * anywhere — including on the toggle itself — and an overlay that covers the
   * page must always be dismissable from the keyboard.
   */
  useEffect(() => {
    if (!isOpen) return;

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  /*
   * Widening the window past the breakpoint drops the sidebar back into the
   * layout, where "open" is meaningless — but the scrim would still be mounted
   * and would swallow every click. Reset the flag instead of leaving a state
   * that only the CSS knows is irrelevant.
   */
  useEffect(() => {
    const query = window.matchMedia('(min-width: 881px)');
    const onChange = (event: MediaQueryListEvent): void => {
      if (event.matches) setIsOpen(false);
    };

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return (
    <SidebarContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarState {
  const context = useContext(SidebarContext);
  if (!context) throw new Error('useSidebar must be used inside a SidebarProvider.');
  return context;
}
